import { useEffect, useState, type FormEvent } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import type { ForgotPasswordOutcome } from '@/services/auth/authService';
import { validateEmail } from '@/utils/formValidators';
import { showToast } from '@/utils/globalToast';
import { AuthLogo } from './AuthLogo';
import { AuthTextField, BackCircleButton, authControlStyles as controls } from './AuthControls';
import styles from './ForgotPasswordForm.module.css';

/**
 * ForgotPasswordForm — the forgot password view, in place of the sign-in form on /login.
 * Mirrors Flutter WebForgotPasswordEmailScreen (web_login/web_forgot_password/web_forgot_password.dart)
 * + WebLoginBloc._ForgotPasswordEmailTriggeredEvent: the round back button top left, the auth
 * logo, the illustration, "Forgot Password ?", Registered Email ID, the rate-limit countdown and
 * Continue. The page's progress overlay covers it while the request runs.
 *
 * Uses the backend endpoint (NOT Firebase sendPasswordResetEmail):
 *   POST /v1/p/account/forget-password { email }
 *
 *   - sent: toast AuthConstants.sentPasswordResetLinkMsg and the form closes; useLogin then
 *     navigates like WebLoginPageConsumer on isSuccess (ProfileInput destination, else /home).
 *     Flutter's inline sentPasswordResetLink text is reset in the same listener, so it never
 *     shows and isn't ported.
 *   - 429: toast ForgotPasswordEmailConstants.forgotPasswordMaxLimit (2 s) and a 1-minute
 *     countdown that keeps Continue disabled
 *   - anything else: toast ErrorConstants.invalidCredentials
 */

interface ForgotPasswordFormProps {
  onBack: () => void;
  onSubmit: (email: string) => Promise<ForgotPasswordOutcome>;
  initialEmail?: string;
}

const RATE_LIMIT_SECONDS = 60;
const EMAIL_ERROR_ID = 'forgot-password-email-error';

/** Flutter's countdown text: '0$minutes:$seconds', seconds not zero-padded ("00:9") */
function formatCountdown(totalSeconds: number): string {
  return `0${Math.floor(totalSeconds / 60)}:${totalSeconds % 60}`;
}

export function ForgotPasswordForm({
  onBack,
  onSubmit,
  initialEmail = '',
}: ForgotPasswordFormProps) {
  const { t } = useLocalization();

  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);

  useEffect(() => {
    if (timerSeconds <= 0) return;
    const id = setTimeout(() => setTimerSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [timerSeconds]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isLoading || timerSeconds > 0) return;

    const trimmed = email.trim();
    const validationError = validateEmail(trimmed, t);
    setError(validationError);
    if (validationError) return;

    setIsLoading(true);
    try {
      const outcome = await onSubmit(trimmed.toLowerCase());
      if (outcome === 'sent') {
        showToast('We have sent reset password link to your registered email id.\nClick on the link to reset a password', 'success');
        onBack();
      } else if (outcome === 'rateLimited') {
        setTimerSeconds(RATE_LIMIT_SECONDS);
        showToast(
          'Your request for forgot password max limit is crossed, please wait and request again.',
          'error',
          2000,
        );
      } else {
        showToast('Invalid login credentials', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.wrapper}>
      <BackCircleButton className={styles.backBtn} onClick={onBack} />

      <div className={styles.content}>
        <AuthLogo className={styles.logo} />

        <div className={styles.illustration}>
          <img src="/assets/icons/forgot_password_illustration_image.svg" alt="" />
        </div>

        <h1 className={styles.title}>{t('Forgot Password ?')}</h1>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <AuthTextField
            id="forgot-password-email"
            label={t('Registered Email ID')}
            type="email"
            autoFocus
            autoComplete="username"
            placeholder={t('Enter your registered Email ID')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
            aria-required="true"
            error={error}
            errorId={EMAIL_ERROR_ID}
            disabled={isLoading}
          />
          <div className={styles.fieldGap} />

          {timerSeconds > 0 && (
            <div className={styles.timer} aria-live="polite">{formatCountdown(timerSeconds)}</div>
          )}

          <button
            type="submit"
            className={`${controls.customButton} ${styles.continueBtn}`}
            disabled={isLoading || timerSeconds > 0}
          >
            {t('Continue')}
          </button>
        </form>
      </div>
    </div>
  );
}
